import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { IS_DEV_ACCOUNT, IS_MAIN_COMPANY, IS_STAGING } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup, Modal, MultiSelect, ToggleButton } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { MembershipType } from '../../PagePermissions'
import { BorrowerQuickLinks } from './BorrowerQuickLinks'
import { defaultFeatureKeys, FeatureData, featureInputs } from './constant'
import { FeatureDetail } from './FeatureDetail'
import { UnderwritingQuickLinksConfig } from './UnderwritingQuickLinksConfig'

const quickLinkTabs = {
  quickLinks: 'Quick Links',
  borrowerQuickLinks: 'Borrower Quick Links',
  underwritingQuickLinks: 'Underwriting Floater Quick Links',
}

const defaultNewFeature: Omit<FeatureData, 'order'> = {
  title: '',
  url: '',
  value: true,
  permissions: [],
  loanStatus: [],
  submenus: [],
}

export const Features = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<Record<string, FeatureData>>({})
  const [loanGlobalStatus, pagePermissions, auth] = useSelector((state: any) => [
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.PagePermissions],
    state.auth,
  ])
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newFeature, setNewFeature] = useState(defaultNewFeature)
  const [newFeatureKey, setNewFeatureKey] = useState('')
  const [editingTitleKey, setEditingTitleKey] = useState<string | null>(null)
  const [tab, setTab] = useState('quickLinks')

  const isEnterprise = pagePermissions.type === MembershipType.Enterprise

  const inputs = featureInputs(loanGlobalStatus)

  const initData = async () => {
    setIsLoading(true)
    const featuresRes = await getAdminConfig('features')
    setData(featuresRes)
    setIsLoading(false)
  }
  useEffect(() => {
    initData()
  }, [])

  const onChange = async (key: string, subKey: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = { ...temp[key], [subKey]: value }
    setData(temp)
  }

  const handleFeatureUpdate = (key: string, updatedData: any) => {
    const temp = cloneDeep(data)
    temp[key] = updatedData
    setData(temp)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    await setAdminConfig('features', data)
    setIsLoading(false)
  }

  const handleAddFeature = async () => {
    if (!newFeatureKey.trim()) {
      toast.error('Please enter a unique key')
      return
    }
    if (!newFeature.title.trim()) {
      toast.error('Please enter a title')
      return
    }
    if (data[newFeatureKey]) {
      toast.error('A quick link with this key already exists')
      return
    }

    const temp = cloneDeep(data)
    const maxOrder = Math.max(...Object.values(temp).map((f) => f.order), 0)
    temp[newFeatureKey] = {
      ...newFeature,
      url: newFeature.url || newFeatureKey,
      order: maxOrder + 1,
    }

    setIsLoading(true)
    await setAdminConfig('features', temp)
    setData(temp)
    setIsLoading(false)
    setShowAddModal(false)
    setNewFeature(defaultNewFeature)
    setNewFeatureKey('')
    toast('Quick link added successfully', { type: 'success' })
  }

  const handleDeleteFeature = async (key: string) => {
    const isConfirmed = await confirm('Are you sure you want to delete this quick link?')
    if (!isConfirmed) return

    const temp = cloneDeep(data)
    const deletedOrder = temp[key].order
    delete temp[key]

    // Re-order remaining items
    Object.keys(temp).forEach((k) => {
      if (temp[k].order > deletedOrder) {
        temp[k].order--
      }
    })

    setIsLoading(true)
    await setAdminConfig('features', temp)
    setData(temp)
    setIsLoading(false)
    toast('Quick link deleted successfully', { type: 'success' })
  }

  const handleTitleBlur = async () => {
    setEditingTitleKey(null)
    await onSubmit()
  }

  const orderOptions = useMemo(() => {
    const options: number[] = []
    Object.keys(data).forEach((_, index) => options.push(index + 1))
    return options
  }, [Object.keys(data).length])

  const changeOrder = async (key: string, newOrder: string) => {
    const temp = cloneDeep(data)
    const currentOrder = temp[key].order
    const targetOrder = parseInt(newOrder)

    // Update all affected items
    Object.keys(temp).forEach((k) => {
      if (k === key) {
        temp[k].order = targetOrder
      } else if (currentOrder < targetOrder) {
        if (temp[k].order > currentOrder && temp[k].order <= targetOrder) {
          temp[k].order--
        }
      } else {
        if (temp[k].order >= targetOrder && temp[k].order < currentOrder) {
          temp[k].order++
        }
      }
    })

    setData(temp)
    setIsLoading(true)
    await setAdminConfig('features', temp)
    setIsLoading(false)
  }

  // Sort data by order
  const sortedData = useMemo(() => {
    return Object.entries(data).sort(([, a], [, b]) => a.order - b.order)
  }, [data])

  // Show detail page if a feature is selected
  if (selectedFeature && data[selectedFeature]) {
    return (
      <FeatureDetail
        featureKey={selectedFeature}
        featureData={data[selectedFeature]}
        allData={data}
        onBack={() => setSelectedFeature(null)}
        onUpdate={handleFeatureUpdate}
      />
    )
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      <ButtonGroup title={quickLinkTabs} value={tab} onChange={setTab} />

      {tab === 'quickLinks' && (
        <>
          <div className="flex justify-end items-center mb-4 mt-4">
            {(isEnterprise || IS_MAIN_COMPANY || IS_STAGING || IS_DEV_ACCOUNT(auth?.profile?.email || '')) && (
              <button
                className="flex items-center gap-1 px-3 py-1.5 bg-shade-blue text-white rounded hover:bg-shade-blue/90"
                onClick={() => setShowAddModal(true)}
              >
                <PlusIcon className="w-4 h-4" />
                Add New
              </button>
            )}
          </div>

          {sortedData.map(([key, value], index: number) => {
            return (
              <div
                className={`flex justify-between items-center p-2 pl-4 ${index % 2 && 'bg-slate-100'}`}
                key={`${key}-${index}`}
              >
                <div className="flex-1 flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <span className="text-sm">{index + 1}.</span>
                    {editingTitleKey === key ? (
                      <input
                        type="text"
                        className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-shade-blue"
                        value={value.title}
                        onChange={(e) => onChange(key, 'title', e.target.value)}
                        onBlur={handleTitleBlur}
                        autoFocus
                        placeholder="Menu Title"
                      />
                    ) : (
                      <span
                        className="text-sm cursor-pointer hover:underline min-w-[200px] px-2 py-1"
                        onClick={() => setEditingTitleKey(key)}
                      >
                        {value.title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <MultiSelect
                      id={`${key}-loanStatus`}
                      title="Loan Status"
                      options={loanGlobalStatus}
                      value={value.loanStatus || []}
                      onChange={(selected) => onChange(key, 'loanStatus', selected)}
                      onBlur={onSubmit}
                      className="w-64"
                    />
                    <MultiSelect
                      id={`${key}-accountTypes`}
                      title="Allowed account types"
                      options={accountTypes}
                      value={value.permissions || []}
                      onChange={(selected) => onChange(key, 'permissions', selected)}
                      onBlur={onSubmit}
                      className="w-64"
                      sort
                      sortKey="value"
                    />
                    <select
                      className="py-1 border-gray-300 text-[15px]"
                      value={value.order.toString()}
                      onChange={(e) => changeOrder(key, e.target.value)}
                      onBlur={onSubmit}
                    >
                      {orderOptions.map((item: any, index1) => {
                        return (
                          <option key={index1} value={`${item}`} disabled={item === index + 1}>
                            {item}
                          </option>
                        )
                      })}
                    </select>
                    <ToggleButton
                      id={`${key}-${index}`}
                      value={value.value}
                      onChange={(value) => onChange(key, 'value', value)}
                      onBlur={onSubmit}
                    />
                    {(isEnterprise || IS_STAGING || IS_MAIN_COMPANY) && (
                      <>
                        <span
                          className={`text-shade-blue cursor-pointer hover-shadow1 p-1 rounded ${
                            ['insurance_quote', 'conditions'].includes(key) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => !['insurance_quote', 'conditions'].includes(key) && setSelectedFeature(key)}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                        {!defaultFeatureKeys.includes(key) && (
                          <span
                            className="text-red-500 cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => handleDeleteFeature(key)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </>
      )}

      {tab === 'borrowerQuickLinks' && <BorrowerQuickLinks />}

      {tab === 'underwritingQuickLinks' && <UnderwritingQuickLinksConfig />}

      {showAddModal && (
        <Modal
          isOpen
          title="Add New Quick Link"
          titleOkay="Add"
          onClose={() => {
            setShowAddModal(false)
            setNewFeature(defaultNewFeature)
            setNewFeatureKey('')
          }}
          onOk={handleAddFeature}
        >
          <div className="space-y-4 w-144">
            {Object.keys(inputs).map((key: string) => {
              const input = inputs[key]
              input.value = (newFeature as any)[key]
              if (key === 'key') input.value = newFeatureKey
              return (
                <RenderInput
                  key={key}
                  input={input as any}
                  Key={`newFeature-${key}`}
                  onChange={(_: string, value: any) => {
                    if (key === 'key') {
                      setNewFeatureKey(value.replace(/\s/g, '_').toLowerCase())
                    } else {
                      setNewFeature({ ...newFeature, [key]: value })
                    }
                  }}
                />
              )
            })}
          </div>
        </Modal>
      )}
    </div>
  )
}
